import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { TravelerItemUI } from '@pax/entities/pax-ui';
import { passengerListUI } from '@pax/mappers/passenger-list-ui.mapper';
import { getDefaultDataIndex, getDefaultPaxData, getDefaultDataPassengerUI } from '@pax/mappers/default-pax.mapper';
import { loadSearchDataSuccessAction, saveSearchDataAction } from '@shared/base-search-bar/store/actions/storage.actions';
import * as actions from '@pax/store/actions/pax.actions';
import {
  setDefaultWithNewLoyalty, setDefaultWithSuccessLoyalty,
  setDefaultWithNewLoyaltySelection,
  setDefaultWithSuccessLoyaltyUpdate,
  setDefaultWithSuccessLoyaltyDelete
} from '@pax/mappers/loyalty.mapper';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

export interface PassengerListUIState extends EntityState<TravelerItemUI> { }

function getId(traveler: TravelerItemUI): number {
  return traveler.id;
}

export const passengerListUIAdapter: EntityAdapter<TravelerItemUI> = createEntityAdapter<TravelerItemUI>({
  selectId: getId
});

export const getEntityById = (id: number) => (state: PassengerListUIState) => state.entities[id];

export const initialState = passengerListUIAdapter.getInitialState({});

const featureReducer = createReducer(
  initialState,
  on(saveSearchDataAction, (state, { data, searchBy }) => {
    if (searchBy === SearchBy.PACKAGES) {
      const arrangement = data ? data?.passengerArrangement : [];
      const passengerArrangement = [].concat(...arrangement);
      return passengerListUIAdapter.setAll(passengerListUI(passengerArrangement, searchBy), state);
    } else return state;
  }),
  on(loadSearchDataSuccessAction, (state, { data, searchBy }) => {
    const arrangement = data ? data.passengerArrangement : [];
    const passengerArrangement = [].concat(...arrangement);
    return passengerListUIAdapter.setAll(passengerListUI(passengerArrangement, searchBy), state);
  }),
  on(actions.configDefaultPassengerUI, (state, { config }) => {
    const { response, user, isInternationalSearch, documents, rules, defaultAirline } = config;
    const defaultDataId = getDefaultDataIndex({ travelers: response, user, isInternationalSearch, documents, rules });
    if (defaultDataId >= 0) {
      const data = getDefaultPaxData(response[defaultDataId], defaultAirline, true);
      return passengerListUIAdapter.updateOne(
        { id: 0, changes: { defaultData: data } }, state
      );
    } else return state;

  }),
  on(actions.setPassengerDefaultDataUIAction, (state, { payload, id, defaultAirline }) => {
    return defaultAirline ?
      passengerListUIAdapter.updateOne({
        id, changes: {
          defaultData: getDefaultPaxData(payload, defaultAirline, false)
        }
      }, state) : passengerListUIAdapter.updateOne({
        id, changes: {
          defaultData: {
            ...payload,
            isConfigDefault: false
          }
        }
      }, state);
  }),
  on(actions.deletePaxSuccessAction, (state, { payload, id }) => {
    const entity = state.entities[id];
    const defaultEntityData = entity ? entity.defaultData : null;
    const defaultData = defaultEntityData && defaultEntityData._id === payload._id ? null : defaultEntityData;
    return passengerListUIAdapter.updateOne({ id, changes: { defaultData } }, state);
  }),
  on(actions.deletePaxDocumentSuccessAction, (state, { payload, id }) => {
    const entity = state.entities[id];
    const defaultData = getDefaultDataPassengerUI(entity, payload.documentId);
    return passengerListUIAdapter.updateOne({ id, changes: { defaultData } }, state);
  }),
  on(actions.setTavelerPaxAction, (state, { config }) => {
    const { payload, id, defaultAirline } = config;
    return defaultAirline ?
      passengerListUIAdapter.updateOne({
        id, changes: {
          defaultData: getDefaultPaxData(payload, defaultAirline, false)
        }
      }, state) : passengerListUIAdapter.updateOne({
        id, changes: {
          defaultData: {
            ...payload,
            isConfigDefault: false
          }
        }
      }, state);
  }),
  on(actions.addLoyaltyProgramAction, (state, { payload, id }) => {
    return passengerListUIAdapter.updateOne({
      id, changes: {
        defaultData: setDefaultWithNewLoyalty(getEntityById(id)(state), payload)
      }
    }, state);
  }),
  on(actions.updateLoyaltyProgramSuccessAction, (state, { response, id }) => {
    return passengerListUIAdapter.updateOne({
      id, changes: {
        defaultData: setDefaultWithSuccessLoyaltyUpdate(getEntityById(id)(state), response)
      }
    }, state);
  }),
  on(actions.createLoyaltyProgramSuccessAction, (state, { response, id }) => {
    return passengerListUIAdapter.updateOne({
      id, changes: {
        defaultData: setDefaultWithSuccessLoyalty(getEntityById(id)(state), response)
      }
    }, state);
  }),
  on(actions.setLoyaltyProgramAction, (state, { payload, id }) => {
    return passengerListUIAdapter.updateOne({
      id, changes: {
        defaultData: setDefaultWithNewLoyaltySelection(getEntityById(id)(state), payload)
      }
    }, state);
  }),
  on(actions.deleteLoyaltyProgramSuccessAction, (state, { response, id }) => {
    return passengerListUIAdapter.updateOne({
      id, changes: {
        defaultData: setDefaultWithSuccessLoyaltyDelete(getEntityById(id)(state), response)
      }
    }, state);
  }),
  on(actions.removeDefaultPaxAction, (state, { id }) => {
    return passengerListUIAdapter.updateOne({ id, changes: { defaultData: null } }, state);
  }),
  on(actions.setPassengerListUiAction, (state, { list }) => {
    return passengerListUIAdapter.setAll(list, state);
  }),
);

export const passengerListUIReducer = (state: PassengerListUIState, action: Action): PassengerListUIState => {
  return featureReducer(state, action);
};
